import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, Briefcase, Crown, Target, UserPlus, Camera, Users, MapPin, BookmarkPlus, Lock, Check } from 'lucide-react';
import { projectId, API_BASE } from '../utils/supabase/info';
import { useDebounce } from '../utils/hooks/useDebounce';
import { NORWEGIAN_COUNTIES } from '../utils/norwegian-locations';
import { motion, AnimatePresence } from 'motion/react';
import { AnimatedFilterIcon, type AnimatedFilterIconHandle } from './AnimatedFilterIcon';
import { CreateSavedSearchModal } from './CreateSavedSearchModal';
import { CreateGigModal } from './CreateGigModal';
import { PageShell } from './layout/PageShell';
import { HomeUserCard } from './HomeUserCard';
import { toast } from '../utils/toast';
import { devLog } from '../utils/logger';
import { Loader } from './ui/Loader';
import { canUseSavedSearches } from '../utils/accessPolicy';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { fetchWithAuth } from '../utils/api';

const HEADER_TEXTS = [
  "Vis hva du kan",
  //"Finn ditt neste talent",
  //"Søk etter lidenskap",
  //"Finn din nye kollega",
  //"Søk blant talenter",
  //"Finn en problemløser",
  //"Søk etter erfaring",
];

interface HomePageProps {
  accessToken: string | null;
  onViewProfile: (userId: string) => void;
  onLoginRequired?: () => void;
  userProfile?: any;
  /** When true (desktop), CreateGigModal is portaled to body so page is visible behind */
  isDesktop?: boolean;
  blockedUserIds?: string[];
  onOpenMyGigs?: () => void;
  onOpenMyInterests?: () => void;
  onOpenGigInterests?: (gigId: string, gigTitle: string) => void;
  createGigModalTrigger?: { gigIdToEdit?: string; openInView?: 'home' | 'gigs' } | null;
  onClearCreateGigModalTrigger?: () => void;
  onNavigateToSubscription?: () => void;
  initialSearchPreset?: {
    id: string;
    searchTerms: string[];
    counties: string[];
  } | null;
  onInitialSearchPresetApplied?: () => void;
}

export function HomePage({ 
  accessToken, 
  onViewProfile, 
  onLoginRequired, 
  userProfile, 
  isDesktop = false,
  blockedUserIds = [], 
  onOpenMyGigs, 
  onOpenMyInterests, 
  onOpenGigInterests, 
  createGigModalTrigger, 
  onClearCreateGigModalTrigger,
  onNavigateToSubscription,
  initialSearchPreset,
  onInitialSearchPresetApplied,
}: HomePageProps) {
  const [users, setUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshingList, setRefreshingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [onlineStatuses, setOnlineStatuses] = useState<Record<string, boolean>>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const countyDropdownRef = useRef<HTMLDivElement>(null);
  const filterIconRef = useRef<AnimatedFilterIconHandle>(null);
  // iOS PWA: JS-based sticky (position:sticky is broken in standalone mode)
  // Uses direct DOM manipulation (no React state) to avoid feedback-loop between
  // IntersectionObserver → setState → re-render → layout shift → observer fires again.
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const hasCheckedOnline = useRef(false);
  const onlineCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onlineCheckInFlightRef = useRef(false);
  const lastOnlineStatusFetchAtRef = useRef(0);
  const hasFetchedInitial = useRef(false);
  const usersFetchRequestIdRef = useRef(0);
  const usersFetchAbortRef = useRef<AbortController | null>(null);
  const usersLoadMoreAbortRef = useRef<AbortController | null>(null);
  const lastUsersBackgroundRefreshAtRef = useRef(0);
  const savedSearchCountFetchInFlightRef = useRef(false);
  const lastSavedSearchCountFetchAtRef = useRef(0);
  const visibleUserIdsRef = useRef<Set<string>>(new Set());
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [savedSearchCount, setSavedSearchCount] = useState(0);
  const enableListVirtualization = (import.meta as any)?.env?.VITE_ENABLE_LIST_VIRTUALIZATION === 'true';
  const enableTrueVirtualization = (import.meta as any)?.env?.VITE_ENABLE_TRUE_VIRTUALIZATION === 'true';
  const [virtualUserLimit, setVirtualUserLimit] = useState(120);
  const [headerText] = useState(() => {
    return HEADER_TEXTS[Math.floor(Math.random() * HEADER_TEXTS.length)];
  });

  // Gig modals state (for create gig modal via Settings)
  const [selectedGig, setSelectedGig] = useState<any>(null);
  const [showCreateGigModal, setShowCreateGigModal] = useState(false);

  useModalFocus(showCreateGigModal, { fullscreen: true });

  // iOS PWA: IntersectionObserver-based sticky (position:sticky broken in standalone mode)
  // Direct DOM manipulation avoids React re-render loop that causes scroll freeze/thrashing.
  useEffect(() => {
    const isIosPwa = document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;

    const sentinel = stickySentinelRef.current;
    const bar = stickyBarRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;

    // Header bottom = fixed header height (includes safe-area via padding)
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;

    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return; // no change — skip DOM work
        isStuck = shouldStick;
        if (shouldStick) {
          // Measure height NOW (not at mount) so it reflects current layout
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          const barH = bar.offsetHeight + mb;
          placeholder.style.height = `${barH}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Listen to createGigModalTrigger from App.tsx (only open here when not explicitly for gigs view)
  useEffect(() => {
    if (createGigModalTrigger && createGigModalTrigger.openInView !== 'gigs') {
      const { gigIdToEdit } = createGigModalTrigger;
      
      if (gigIdToEdit) {
        // Fetch gig data and open edit modal
        (async () => {
          if (!accessToken) return;
          
          try {
            const response = await fetch(
              `${API_BASE}/gigs/${gigIdToEdit}`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              setSelectedGig(data.gig);
              setShowCreateGigModal(true);
            } else {
              toast.error('Kunne ikke hente oppdrag');
            }
          } catch (error) {
            toast.error('Kunne ikke hente oppdrag');
          }
        })();
      } else {
        // Open create modal
        setShowCreateGigModal(true);
      }
      
      // Clear trigger after opening modal
      if (onClearCreateGigModalTrigger) {
        onClearCreateGigModalTrigger();
      }
    }
  }, [createGigModalTrigger]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1280);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animate filter icon based on dropdown state
  useEffect(() => {
    if (filterIconRef.current) {
      if (showCountyDropdown) {
        filterIconRef.current.startAnimation();
      } else {
        filterIconRef.current.stopAnimation();
      }
    }
  }, [showCountyDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countyDropdownRef.current && !countyDropdownRef.current.contains(event.target as Node)) {
        setShowCountyDropdown(false);
      }
    };

    if (showCountyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountyDropdown]);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 500);
  const BACKGROUND_REFRESH_COOLDOWN_MS = 12000;
  const SAVED_SEARCH_COUNT_COOLDOWN_MS = 15000;
  const requestBackgroundUsersRefresh = () => {
    const now = Date.now();
    if (now - lastUsersBackgroundRefreshAtRef.current < BACKGROUND_REFRESH_COOLDOWN_MS) {
      return;
    }
    lastUsersBackgroundRefreshAtRef.current = now;
    hasFetchedInitial.current = false;
    void fetchUsers(true, true);
  };

  const buildUsersSearchParams = useCallback((offset: number) => {
    const params = new URLSearchParams();
    const searchTerm = debouncedSearch.trim();
    if (searchTerm) {
      params.append('skill', searchTerm);
    }
    if (selectedCounties.length > 0) {
      params.append('counties', selectedCounties.join(','));
    }
    params.append('limit', '20');
    params.append('offset', String(offset));
    return params;
  }, [debouncedSearch, selectedCounties]);

  const areSkillListsEquivalent = useCallback((prevSkills: string[] = [], nextSkills: string[] = []) => {
    if (prevSkills.length !== nextSkills.length) return false;
    for (let i = 0; i < prevSkills.length; i += 1) {
      if (prevSkills[i] !== nextSkills[i]) return false;
    }
    return true;
  }, []);

  const areUsersEquivalentForRender = useCallback((prevUser: any, nextUser: any) => {
    return (
      prevUser?.id === nextUser?.id &&
      prevUser?.updatedAt === nextUser?.updatedAt &&
      prevUser?.name === nextUser?.name &&
      prevUser?.location === nextUser?.location &&
      prevUser?.county === nextUser?.county &&
      prevUser?.bio === nextUser?.bio &&
      prevUser?.profileImage === nextUser?.profileImage &&
      prevUser?.showcaseImage === nextUser?.showcaseImage &&
      prevUser?.portfolioCount === nextUser?.portfolioCount &&
      areSkillListsEquivalent(prevUser?.skills || [], nextUser?.skills || []) &&
      areSkillListsEquivalent(prevUser?.matchingSkills || [], nextUser?.matchingSkills || [])
    );
  }, [areSkillListsEquivalent]);

  const reconcileUserList = useCallback((prevUsers: any[], nextUsers: any[]) => {
    if (!prevUsers.length) return nextUsers;
    const prevById = new Map(prevUsers.map((user) => [user.id, user]));
    const merged = nextUsers.map((nextUser) => {
      const prevUser = prevById.get(nextUser.id);
      if (!prevUser) return nextUser;
      return areUsersEquivalentForRender(prevUser, nextUser) ? prevUser : nextUser;
    });
    const unchanged =
      prevUsers.length === merged.length &&
      merged.every((user, index) => user === prevUsers[index]);
    return unchanged ? prevUsers : merged;
  }, [areUsersEquivalentForRender]);

  // Apply one-time preset from Saved Searches when navigating to Home
  useEffect(() => {
    if (!initialSearchPreset) return;

    const firstSearchTerm = (initialSearchPreset.searchTerms || [])
      .map((term) => term?.toString().trim())
      .find(Boolean) || '';
    const nextCounties = (initialSearchPreset.counties || []).filter(Boolean);

    setSearchQuery(firstSearchTerm);
    setSelectedCounties(nextCounties);
    setShowCountyDropdown(false);
    hasFetchedInitial.current = false;
    onInitialSearchPresetApplied?.();
  }, [initialSearchPreset, onInitialSearchPresetApplied]);

  // Reset pagination when search changes
  useEffect(() => {
    setUsers([]);
    setHasMore(false);
    hasFetchedInitial.current = false;
    fetchUsers(true, false);
  }, [debouncedSearch, selectedCounties]);

  // Refetch when user comes back to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        devLog('👁️ Page visible again - refreshing search results');
        requestBackgroundUsersRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!accessToken || userProfile?.userType !== 'employer') {
      setSavedSearchCount(0);
      return;
    }
    void fetchSavedSearchCount();
  }, [accessToken, userProfile?.userType]);

  useEffect(() => {
    const handleInvalidation = () => {
      requestBackgroundUsersRefresh();
      if (accessToken && userProfile?.userType === 'employer') {
        void fetchSavedSearchCount();
      }
    };

    window.addEventListener('talentfunn:profile-updated', handleInvalidation);
    window.addEventListener('talentfunn:posts-updated', handleInvalidation);
    window.addEventListener('talentfunn:favorites-updated', handleInvalidation);
    window.addEventListener('talentfunn:saved-searches-updated', handleInvalidation);

    return () => {
      window.removeEventListener('talentfunn:profile-updated', handleInvalidation);
      window.removeEventListener('talentfunn:posts-updated', handleInvalidation);
      window.removeEventListener('talentfunn:favorites-updated', handleInvalidation);
      window.removeEventListener('talentfunn:saved-searches-updated', handleInvalidation);
    };
  }, [accessToken, debouncedSearch, selectedCounties, userProfile?.userType]);

  const filteredUsers = useMemo(
    () => users.filter(user => !blockedUserIds.includes(user.id)),
    [users, blockedUserIds]
  );
  const visibleUsers = useMemo(
    () => (enableListVirtualization ? filteredUsers.slice(0, virtualUserLimit) : filteredUsers),
    [enableListVirtualization, filteredUsers, virtualUserLimit]
  );

  // True virtualization (windowing) is only enabled on mobile where the list is single-column.
  // Keep existing slice-based virtualization as the default to avoid grid regressions.
  const enableWindowVirtualization = enableListVirtualization && enableTrueVirtualization && isMobile;
  const usersForRender = enableWindowVirtualization ? filteredUsers : visibleUsers;
  const usersVirtualizer = useWindowVirtualizer({
    count: enableWindowVirtualization ? usersForRender.length : 0,
    estimateSize: () => 560,
    overscan: 6,
  });

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current || loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && enableListVirtualization && virtualUserLimit < filteredUsers.length) {
          setVirtualUserLimit((prev) => Math.min(prev + 120, filteredUsers.length));
          return;
        }
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreUsers();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [enableListVirtualization, filteredUsers.length, hasMore, loading, loadingMore, virtualUserLimit]);

  useEffect(() => {
    setVirtualUserLimit(120);
  }, [debouncedSearch, selectedCounties, enableListVirtualization]);

  const checkOnlineStatuses = useCallback(async () => {
    if (filteredUsers.length === 0 || !accessToken) return;
    if (document.visibilityState !== 'visible') return;
    if (onlineCheckInFlightRef.current) return;

    const now = Date.now();
    const MIN_ONLINE_FETCH_GAP_MS = 20000;
    if (now - lastOnlineStatusFetchAtRef.current < MIN_ONLINE_FETCH_GAP_MS) return;
    
    try {
      onlineCheckInFlightRef.current = true;
      const visibleUserIds = Array.from(visibleUserIdsRef.current);
      const userIds =
        visibleUserIds.length > 0
          ? visibleUserIds.slice(0, 40)
          : filteredUsers.slice(0, 40).map((u) => u.id);
      if (userIds.length === 0) return;
      const response = await fetchWithAuth(
        `${API_BASE}/check-online`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
          body: JSON.stringify({ userIds }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOnlineStatuses(data.onlineStatuses);
        lastOnlineStatusFetchAtRef.current = Date.now();
      } else if (response.status === 401 || response.status === 403) {
        // Session refresh is handled centrally in fetchWithAuth; avoid noisy retries here.
        devLog('Skipping online status update due to auth state transition');
      }
    } catch (error) {
      devLog('Error checking online statuses:', error);
    } finally {
      onlineCheckInFlightRef.current = false;
    }
  }, [accessToken, filteredUsers]);

  useEffect(() => {
    visibleUserIdsRef.current.clear();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const userId = (entry.target as HTMLElement).dataset.userId;
          if (!userId) continue;
          if (entry.isIntersecting) {
            visibleUserIdsRef.current.add(userId);
          } else {
            visibleUserIdsRef.current.delete(userId);
          }
        }
      },
      { threshold: 0.2 }
    );

    const cardElements = document.querySelectorAll('[data-home-user-card="true"]');
    cardElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [filteredUsers]);

  useEffect(() => {
    // Clear existing interval first
    if (onlineCheckIntervalRef.current) {
      clearInterval(onlineCheckIntervalRef.current);
    }
    
    if (filteredUsers.length > 0) {
      // Check immediately
      void checkOnlineStatuses();
        // Check every 120 seconds (online status is approximate).
      onlineCheckIntervalRef.current = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        void checkOnlineStatuses();
        }, 120000);
    }
    
    return () => {
      if (onlineCheckIntervalRef.current) {
        clearInterval(onlineCheckIntervalRef.current);
        onlineCheckIntervalRef.current = null;
      }
    };
  }, [filteredUsers.length, checkOnlineStatuses]);

  useEffect(() => {
    return () => {
      usersFetchAbortRef.current?.abort();
      usersLoadMoreAbortRef.current?.abort();
    };
  }, []);

  async function fetchUsers(reset: boolean = false, backgroundRefresh: boolean = false) {
    if (hasFetchedInitial.current && !reset) return;
    hasFetchedInitial.current = true;
    const requestId = ++usersFetchRequestIdRef.current;
    const shouldShowBlockingLoader = !backgroundRefresh || users.length === 0;

    if (shouldShowBlockingLoader) {
      setLoading(true);
      setRefreshingList(false);
    } else {
      setRefreshingList(true);
    }
    try {
      const searchTerm = debouncedSearch.trim();
      const params = buildUsersSearchParams(0);
      const url = `${API_BASE}/users?${params.toString()}`;
      const startedAt = performance.now();

      devLog('🔍 Søker:', { searchTerm, url });

      usersFetchAbortRef.current?.abort();
      const controller = new AbortController();
      usersFetchAbortRef.current = controller;
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        devLog('❌ Error fetching users:', await response.text());
        return;
      }

      const data = await response.json();
      if (requestId !== usersFetchRequestIdRef.current) return;
      devLog('✅ Mottok data:', { count: data.users?.length || 0, users: data.users });
      
      const usersWithSortedSkills = data.users?.map((user: any) => {
        if (searchTerm && user.skills && user.skills.length > 0) {
          const lowerSearchTerm = searchTerm.toLowerCase();
          const sortedSkills = [...user.skills].sort((a: string, b: string) => {
            const aMatches = a.toLowerCase().includes(lowerSearchTerm);
            const bMatches = b.toLowerCase().includes(lowerSearchTerm);
            
            if (aMatches && !bMatches) return -1;
            if (!aMatches && bMatches) return 1;
            return 0;
          });
          
          return { ...user, skills: sortedSkills };
        }
        return user;
      }) || [];
      
      devLog('📊 Viser brukere:', usersWithSortedSkills.length);
      setUsers((prevUsers) => reconcileUserList(prevUsers, usersWithSortedSkills));
      setHasMore(data.hasMore);
      devLog('⏱️ Home users refreshed', { durationMs: Math.round(performance.now() - startedAt), count: usersWithSortedSkills.length });
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      devLog('❌ Error fetching users:', error);
    } finally {
      if (requestId === usersFetchRequestIdRef.current) {
        setLoading(false);
        setRefreshingList(false);
      }
    }
  }

  async function fetchSavedSearchCount() {
    if (!accessToken || userProfile?.userType !== 'employer') return;
    if (savedSearchCountFetchInFlightRef.current) return;
    const now = Date.now();
    if (now - lastSavedSearchCountFetchAtRef.current < SAVED_SEARCH_COUNT_COOLDOWN_MS) return;
    savedSearchCountFetchInFlightRef.current = true;
    lastSavedSearchCountFetchAtRef.current = now;
    try {
      const response = await fetch(`${API_BASE}/saved-searches`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        if (response.status === 403) {
          setSavedSearchCount(0);
        }
        return;
      }
      const data = await response.json();
      setSavedSearchCount(Array.isArray(data.savedSearches) ? data.savedSearches.length : 0);
    } catch (error) {
      devLog('Error fetching saved search count:', error);
    } finally {
      savedSearchCountFetchInFlightRef.current = false;
    }
  }

  async function loadMoreUsers() {
    setLoadingMore(true);
    try {
      const searchTerm = debouncedSearch.trim();
      const params = buildUsersSearchParams(users.length);

      const url = `${API_BASE}/users?${params.toString()}`;
      const startedAt = performance.now();

      devLog('📄 Laster mer:', { offset: users.length, url });

      usersLoadMoreAbortRef.current?.abort();
      const controller = new AbortController();
      usersLoadMoreAbortRef.current = controller;
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        devLog('❌ Error fetching users:', await response.text());
        setLoadingMore(false);
        return;
      }

      const data = await response.json();
      devLog('✅ Mottok data:', { count: data.users?.length || 0, hasMore: data.hasMore });
      
      const usersWithSortedSkills = data.users?.map((user: any) => {
        if (searchTerm && user.skills && user.skills.length > 0) {
          const lowerSearchTerm = searchTerm.toLowerCase();
          const sortedSkills = [...user.skills].sort((a: string, b: string) => {
            const aMatches = a.toLowerCase().includes(lowerSearchTerm);
            const bMatches = b.toLowerCase().includes(lowerSearchTerm);
            
            if (aMatches && !bMatches) return -1;
            if (!aMatches && bMatches) return 1;
            return 0;
          });
          
          return { ...user, skills: sortedSkills };
        }
        return user;
      }) || [];
      
      setUsers(prevUsers => [...prevUsers, ...usersWithSortedSkills]);
      setHasMore(data.hasMore);
      devLog('⏱️ Home load-more completed', { durationMs: Math.round(performance.now() - startedAt), added: usersWithSortedSkills.length });
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      devLog('❌ Error fetching users:', error);
    } finally {
      setLoadingMore(false);
    }
  }

  const avatarColors = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-orange-500 to-red-500',
    'from-green-500 to-emerald-500',
    'from-yellow-500 to-orange-500',
    'from-indigo-500 to-purple-500',
  ];

  const getAvatarColor = (index: number) => avatarColors[index % avatarColors.length];

  const createGigModalEl = showCreateGigModal ? (
    <CreateGigModal
      gig={selectedGig}
      onClose={() => {
        setShowCreateGigModal(false);
        setSelectedGig(null);
      }}
      onSuccess={() => {
        setShowCreateGigModal(false);
        setSelectedGig(null);
      }}
      userProfile={userProfile}
      accessToken={accessToken || ''}
      onNavigateToSubscription={onNavigateToSubscription}
    />
  ) : null;

  if (showCreateGigModal && !isDesktop) {
    return createGigModalEl;
  }

  return (
    <>
      {showCreateGigModal && isDesktop && createGigModalEl && ReactDOM.createPortal(createGigModalEl, document.body)}
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <PageShell className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 pt-5 xl:pt-5 xl:pb-24">
          {/* Title only – scrolls away when user scrolls down */}
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl xl:text-5xl bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-2">
              {headerText}
            </h1>
          </div>

          {/* Sentinel: marks the natural position of the search bar for iOS PWA IntersectionObserver */}
          <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />

          {/* Sticky search bar – sticks under global header when scrolling.
              iOS PWA: .is-stuck toggles to position:fixed via JS (position:sticky broken in standalone). */}
          <div ref={stickyBarRef} className="sticky-search-bar sticky top-0 z-[var(--z-sticky)] -mx-4 px-4 sm:-mx-6 sm:px-6 xl:-mx-8 xl:px-8 pt-2 pb-4 mb-8">
            <div className="max-w-2xl mx-auto">
              <div className="relative group">
                {/* Gradient glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
                
                {/* Search bar */}
                <div className="relative bg-white rounded-2xl shadow-xl flex items-center gap-2">
                  <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-purple-400 w-6 h-6 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchInputRef.current?.blur();
                      }
                    }}
                    placeholder="Søk etter ferdigheter..."
                    className="w-full pl-16 pr-24 py-5 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 text-lg transition-all"
                    ref={searchInputRef}
                  />
                  
                  {/* Save search button (Employer Plus only) */}
                  {!showSaveSearchModal && !showCreateGigModal && userProfile?.userType === 'employer' && (searchQuery || selectedCounties.length > 0) && (
                    <div className="absolute right-14 top-1/2 transform -translate-y-1/2 z-[100]">
                      <motion.button
                        onClick={() => {
                          if (!accessToken) {
                            if (onLoginRequired) onLoginRequired();
                            return;
                          }
                          if (!canUseSavedSearches(userProfile)) {
                            toast.warning(
                              'Oppgrader til Pluss',
                              'Lagrede søk krever Arbeidsgiver Pluss-abonnement',
                              {
                                action: {
                                  label: 'Se abonnement',
                                  onClick: () => onNavigateToSubscription?.()
                                }
                              }
                            );
                            return;
                          }
                          setShowSaveSearchModal(true);
                        }}
                        className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors relative ${
                          canUseSavedSearches(userProfile)
                            ? 'text-purple-600 hover:bg-purple-50'
                            : 'text-gray-400 cursor-not-allowed opacity-60'
                        }`}
                        whileTap={{ scale: canUseSavedSearches(userProfile) ? 0.9 : 1 }}
                        transition={{ duration: 0.2 }}
                        title={canUseSavedSearches(userProfile) ? 'Lagre søk' : 'Krever Pluss-abonnement'}
                      >
                        <BookmarkPlus className="w-5 h-5" />
                        {!canUseSavedSearches(userProfile) && (
                          <Lock className="w-3 h-3 absolute top-0 right-0 text-gray-500" />
                        )}
                      </motion.button>
                    </div>
                  )}
                  
                  {/* County filter button */}
                  {!showSaveSearchModal && !showCreateGigModal && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-[100]" ref={countyDropdownRef}>
                    <motion.button
                      onClick={() => setShowCountyDropdown(!showCountyDropdown)}
                      className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors ${
                        showCountyDropdown 
                          ? 'bg-purple-100 text-purple-600' 
                          : 'text-purple-400 hover:bg-purple-50'
                      }`}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <AnimatedFilterIcon size={24} ref={filterIconRef} />
                    </motion.button>
                    
                    {/* Dropdown menu */}
                    <AnimatePresence>
                      {showCountyDropdown && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                          className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl bg-white shadow-lg border border-purple-100 focus:outline-none z-[10000] max-h-96 overflow-y-auto"
                        >
                          <div className="p-2">
                            <div className="px-3 py-2.5 text-xs font-medium text-purple-600 border-b border-purple-100 mb-1">
                              {selectedCounties.length > 0 ? (
                                <button
                                  onClick={() => setSelectedCounties([])}
                                  className="text-purple-600 hover:text-purple-700 font-semibold"
                                >
                                  Nullstill alle
                                </button>
                              ) : (
                                'Velg ett eller flere fylker'
                              )}
                            </div>
                            {NORWEGIAN_COUNTIES.map((county) => (
                              <label
                                key={county}
                                className="flex items-center px-3 py-2.5 text-sm text-gray-700 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedCounties.includes(county)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCounties([...selectedCounties, county]);
                                    } else {
                                      setSelectedCounties(selectedCounties.filter((c) => c !== county));
                                    }
                                  }}
                                  className="mr-3 w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-2 focus:ring-purple-500 accent-purple-600"
                                />
                                <span className="flex-1">{county}</span>
                                {selectedCounties.includes(county) && (
                                  <Check className="w-4 h-4 text-purple-600 shrink-0" strokeWidth={2.5} />
                                )}
                              </label>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* iOS PWA: placeholder to prevent content jump when search bar switches to position:fixed.
              Always rendered (height toggled via DOM to avoid React re-render feedback loop). */}
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />

          {/* Users grid */}
          {loading ? (
            <div className="text-center py-12">
              <Loader size="xl" className="text-purple-600 mx-auto" />
              <p className="mt-4 text-gray-700">Finner talenter...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md p-8">
              <div className="max-w-md mx-auto">
                <div className="flex justify-center mb-4">
                  {searchQuery ? (
                    <Users className="w-16 h-16 text-gray-400" />
                  ) : (
                    <Target className="w-16 h-16 text-purple-600" />
                  )}
                </div>
                <h3 className="text-2xl mb-3 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                  {searchQuery ? 'Ingen match' : 'Vær den første!'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery
                    ? 'Ingen talenter matcher søket ditt. Prøv andre søkeord.'
                    : 'Ingen komplette profiler ennå. For å vises her må du:'}
                </p>
                {!searchQuery && (
                  <div className="text-gray-600 space-y-2 text-center max-w-xs mx-auto">
                    <div className="flex items-start gap-2">
                      <UserPlus className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <p>Fylle ut bio og legge til ferdigheter</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Camera className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <p>Laste opp showcase-bilde på profilen</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Briefcase className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <p>Publisere minst ett portfolio-prosjekt</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
                {refreshingList && filteredUsers.length > 0 && (
                  <div className="xl:col-span-2 2xl:col-span-3 text-sm text-purple-700 animate-pulse mb-1">
                    Oppdaterer søket...
                  </div>
                )}
                {enableWindowVirtualization ? (
                  <div className="xl:col-span-2 2xl:col-span-3">
                    <div
                      style={{
                        height: `${usersVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {usersVirtualizer.getVirtualItems().map((virtualRow) => {
                        const user = usersForRender[virtualRow.index];
                        if (!user) return null;
                        return (
                          <div
                            key={user.id}
                            ref={usersVirtualizer.measureElement}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              transform: `translateY(${virtualRow.start}px)`,
                              paddingBottom: '24px',
                            }}
                          >
                            <HomeUserCard
                              user={user}
                              avatarGradient={getAvatarColor(virtualRow.index)}
                              isOnline={!!onlineStatuses[user.id]}
                              onViewProfile={onViewProfile}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  visibleUsers.map((user, index) => (
                    <HomeUserCard
                      key={user.id}
                      user={user}
                      avatarGradient={getAvatarColor(index)}
                      isOnline={!!onlineStatuses[user.id]}
                      onViewProfile={onViewProfile}
                    />
                  ))
                )}
              </div>

              {/* Load more indicator */}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="text-center py-8 mt-6"
                >
                  {loadingMore ? (
                    <Loader size="xl" className="text-purple-600" />
                  ) : (
                    <p className="text-gray-700">Last inn flere...</p>
                  )}
                </div>
              )}
            </>
          )}
        </PageShell>

      {/* Save Search Modal */}
      {showSaveSearchModal && ReactDOM.createPortal(
        <CreateSavedSearchModal
          isOpen={showSaveSearchModal}
          onClose={() => setShowSaveSearchModal(false)}
          searchTerms={searchQuery ? [searchQuery] : []}
          counties={selectedCounties}
          accessToken={accessToken || ''}
          currentSavedSearchCount={savedSearchCount}
          onSuccess={() => {
            setShowSaveSearchModal(false);
            setSavedSearchCount(prev => prev + 1);
            window.dispatchEvent(new Event('talentfunn:saved-searches-updated'));
          }}
          onNavigateToSubscription={onNavigateToSubscription}
        />,
        document.body
      )}
    </div>
    </>
  );
}
